import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { MemberExtended, parseCommentSegments } from "@nova/schemas"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import classNames from "classnames"
import { MentionPicker, filterMentionCandidates } from "./MentionPicker"
import { LINK_TOKEN_ATTR, serializeEditor, TOKEN_ATTR } from "./commentEditorSerialization"

export type CommentEditorProps = {
    value: string
    onChange: (value: string) => void
    onSubmit: () => void
    placeholder?: string
    disabled?: boolean
    mentionDirection?: "up" | "down"
}

export type CommentEditorHandle = {
    insertMentionTrigger: () => void
    insertText: (text: string) => void
}

type MentionState = {
    query: string
    range: Range
    container: { top: number; bottom: number; left: number; width: number }
}

const PASTE_URL = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i

const mentionMenuGap = 8

const nameForUserId = (members: MemberExtended[], userId: string) =>
    members.find((member) => member.userId === userId)?.fullName ?? "user"

const buildChip = (userId: string, name: string) => {
    const chip = document.createElement("span")
    chip.setAttribute(TOKEN_ATTR, userId)
    chip.contentEditable = "false"
    chip.className = "text-blue-400 font-medium"
    chip.textContent = `@${name}`
    return chip
}

const buildLinkChip = (label: string, url: string) => {
    const chip = document.createElement("span")
    chip.setAttribute(LINK_TOKEN_ATTR, url)
    chip.className = "text-blue-400 underline"
    chip.textContent = label
    return chip
}

const hydrate = (root: HTMLElement, text: string, members: MemberExtended[]) => {
    root.replaceChildren()
    for (const segment of parseCommentSegments(text)) {
        if (segment.type === "mention") {
            root.append(buildChip(segment.userId, nameForUserId(members, segment.userId)))
        } else if (segment.type === "link") {
            root.append(buildLinkChip(segment.label, segment.url))
        } else {
            root.append(document.createTextNode(segment.value))
        }
    }
}

const isChip = (node: Node | null): boolean =>
    node instanceof HTMLElement && node.hasAttribute(TOKEN_ATTR)

/** The mention menu hangs off the whole message container, not the caret. */
const measureContainer = (root: HTMLElement) => {
    const { top, bottom, left, width } = (root.closest("form") ?? root).getBoundingClientRect()
    return { top, bottom, left, width }
}

const readMentionQuery = (root: HTMLElement): Omit<MentionState, "container"> | null => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return null
    const range = selection.getRangeAt(0)
    if (!range.collapsed) return null

    const node = range.startContainer
    if (node.nodeType !== Node.TEXT_NODE || !root.contains(node)) return null
    if (isChip(node.parentElement)) return null

    const before = (node.textContent ?? "").slice(0, range.startOffset)
    const at = before.lastIndexOf("@")
    if (at === -1) return null
    if (at > 0 && /[\p{L}\p{N}_]/u.test(before[at - 1]!)) return null
    const fragment = before.slice(at + 1)
    if (/\s/.test(fragment)) return null
    if (at === 0 && isChip(node.previousSibling)) return null

    const queryRange = document.createRange()
    queryRange.setStart(node, at)
    queryRange.setEnd(node, range.startOffset)

    return { query: fragment, range: queryRange }
}

export const CommentEditor = forwardRef<CommentEditorHandle, CommentEditorProps>(
    function CommentEditor(
        {
            value,
            onChange,
            onSubmit,
            placeholder = "Leave a comment...",
            disabled,
            mentionDirection = "down",
        },
        ref
    ) {
        const editorRef = useRef<HTMLDivElement>(null)
        const lastSerialized = useRef<string>("")
        const members = useAppSelector(selectWorkspaceMembers)
        const [mention, setMention] = useState<MentionState | null>(null)
        const [activeIndex, setActiveIndex] = useState(0)

        useEffect(() => {
            const root = editorRef.current
            if (!root) return
            if (value === lastSerialized.current) return
            hydrate(root, value, members)
            lastSerialized.current = value
        }, [value, members])

        useEffect(() => {
            editorRef.current?.focus()
        }, [])

        const emit = () => {
            const root = editorRef.current
            if (!root) return
            const serialized = serializeEditor(root)
            lastSerialized.current = serialized
            onChange(serialized)
        }

        const syncMention = () => {
            const root = editorRef.current
            if (!root) return
            const next = readMentionQuery(root)
            if (next?.query !== mention?.query) setActiveIndex(0)
            setMention(next && { ...next, container: measureContainer(root) })
        }

        const focusCaret = () => {
            const root = editorRef.current
            if (!root) return null
            root.focus()
            const selection = window.getSelection()
            if (!selection || selection.rangeCount === 0 || !root.contains(selection.anchorNode)) {
                const end = document.createRange()
                end.selectNodeContents(root)
                end.collapse(false)
                selection?.removeAllRanges()
                selection?.addRange(end)
            }
            return root
        }

        useImperativeHandle(ref, () => ({
            insertMentionTrigger: () => {
                if (!focusCaret()) return
                document.execCommand("insertText", false, "@")
                emit()
                syncMention()
            },
            insertText: (text: string) => {
                if (!focusCaret()) return
                document.execCommand("insertText", false, text)
                emit()
                syncMention()
            },
        }))

        const matches = mention ? filterMentionCandidates(members, mention.query) : []

        const selectMention = (member: MemberExtended) => {
            const root = editorRef.current
            if (!root || !mention) return
            mention.range.deleteContents()
            const chip = buildChip(member.userId, member.fullName)
            mention.range.insertNode(chip)

            const spacer = document.createTextNode(" ")
            chip.after(spacer)

            const caret = document.createRange()
            caret.setStartAfter(spacer)
            caret.collapse(true)

            const selection = window.getSelection()
            selection?.removeAllRanges()
            selection?.addRange(caret)

            setMention(null)
            emit()
        }

        const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (mention && matches.length > 0) {
                if (e.key === "ArrowDown") {
                    e.preventDefault()
                    setActiveIndex((index) => Math.min(index + 1, matches.length - 1))
                    return
                }
                if (e.key === "ArrowUp") {
                    e.preventDefault()
                    setActiveIndex((index) => Math.max(index - 1, 0))
                    return
                }
                if (e.key === "Enter") {
                    e.preventDefault()
                    selectMention(matches[activeIndex])
                    return
                }
                if (e.key === "Escape") {
                    e.preventDefault()
                    // Keep this Escape from also discarding the draft comment.
                    e.stopPropagation()
                    setMention(null)
                    return
                }
            }
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                onSubmit()
            }
        }

        const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
            const pasted = e.clipboardData.getData("text/plain").trim()
            if (!PASTE_URL.test(pasted)) return

            const selection = window.getSelection()
            const root = editorRef.current
            if (!selection || selection.rangeCount === 0 || !root) return
            const range = selection.getRangeAt(0)
            if (range.collapsed || !root.contains(range.commonAncestorContainer)) return

            e.preventDefault()
            const label = range.toString()
            const url = pasted.startsWith("www.") ? `https://${pasted}` : pasted
            range.deleteContents()
            const chip = buildLinkChip(label, url)
            const spacer = document.createTextNode(" ")
            range.insertNode(spacer)
            range.insertNode(chip)

            const caret = document.createRange()
            caret.setStartAfter(spacer)
            caret.collapse(true)
            selection.removeAllRanges()
            selection.addRange(caret)

            emit()
        }

        return (
            <div className="relative w-full">
                <div
                    ref={editorRef}
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    role="textbox"
                    aria-multiline
                    data-placeholder={placeholder}
                    onInput={() => {
                        emit()
                        syncMention()
                    }}
                    onKeyUp={syncMention}
                    onMouseUp={syncMention}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onBlur={() => setMention(null)}
                    className={classNames(
                        "outline-none w-full min-h-5 whitespace-pre-wrap break-words text-white text-[14px] leading-5",
                        "empty:before:content-[attr(data-placeholder)] empty:before:text-gray-300",
                        disabled ? "cursor-not-allowed" : "cursor-text"
                    )}
                />
                {mention &&
                    matches.length > 0 &&
                    createPortal(
                        <div
                            className="fixed z-[9999]"
                            style={{
                                left: mention.container.left,
                                width: mention.container.width,
                                ...(mentionDirection === "up"
                                    ? {
                                          bottom:
                                              window.innerHeight -
                                              mention.container.top +
                                              mentionMenuGap,
                                      }
                                    : { top: mention.container.bottom + mentionMenuGap }),
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <MentionPicker
                                query={mention.query}
                                activeIndex={activeIndex}
                                onSelect={selectMention}
                            />
                        </div>,
                        document.getElementById("portal") ?? document.body
                    )}
            </div>
        )
    }
)
